import React, { useState, useEffect, useRef } from "react";
import { Tracker } from "@/models/tracker";
import {
  Chip,
  IconButton,
  Tooltip,
  Popover,
  Autocomplete,
  Typography,
} from "@/components/ui";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { useTrackerTags } from "@/hooks/use-tracker-tags";
import { useTags } from "@/api/tag";
import { getTagColor } from "@/utils/tag-color";

interface TrackerTagManagerInlineProps {
  tracker: Tracker;
  trackersTagsMap: Record<string, string[]>;
  onTagsUpdate?: (trackerId: string, tags: string[]) => void;
  size?: "small" | "medium";
}

const TrackerTagManagerInline: React.FC<TrackerTagManagerInlineProps> = ({
  tracker,
  trackersTagsMap,
  onTagsUpdate,
  size = "small",
}) => {
  const { tags, isLoading: isLoadingTags, toggleTag, updateTags } = useTrackerTags(tracker.id);
  const { data: allTags } = useTags();
  const [tagPopoverAnchor, setTagPopoverAnchor] = useState<HTMLElement | null>(null);
  const [selectedTagNames, setSelectedTagNames] = useState<string[]>([]);
  // Utiliser un ref pour maintenir la référence à l'élément d'ancrage même après re-render
  const tagPopoverAnchorRef = useRef<HTMLElement | null>(null);
  // Sauvegarder la position de l'ancre pour utiliser anchorPosition si l'élément est perdu
  const [anchorPosition, setAnchorPosition] = useState<{ top: number; left: number } | undefined>(undefined);
  // Utiliser un état séparé pour gérer l'ouverture du popover
  const [isOpen, setIsOpen] = useState(false);

  // Utiliser un ref pour comparer les tags précédents et éviter les mises à jour inutiles
  const prevTagsRef = useRef<string[]>([]);
  useEffect(() => {
    if (onTagsUpdate && tags) {
      // Comparer les tags pour éviter les mises à jour inutiles
      const prevTags = prevTagsRef.current;
      const hasChanged = 
        prevTags.length !== tags.length ||
        !prevTags.every((tag, i) => tag === tags[i]);
      
      if (hasChanged) {
        prevTagsRef.current = tags;
        onTagsUpdate(tracker.id, tags);
      }
    } else if (!tags || tags.length === 0) {
      // Si tags est vide maintenant mais ne l'était pas avant
      if (prevTagsRef.current.length > 0) {
        prevTagsRef.current = [];
        if (onTagsUpdate) {
          onTagsUpdate(tracker.id, []);
        }
      }
    }
  }, [tags, tracker.id, onTagsUpdate]);

  useEffect(() => {
    if (tags && tags.length > 0) {
      setSelectedTagNames(tags);
    } else {
      setSelectedTagNames([]);
    }
  }, [tags]);

  // Maintenir la référence à l'ancre même après re-render
  useEffect(() => {
    if (tagPopoverAnchor) {
      tagPopoverAnchorRef.current = tagPopoverAnchor;
    }
  }, [tagPopoverAnchor]);

  // Synchroniser isOpen avec tagPopoverAnchor et anchorPosition
  useEffect(() => {
    setIsOpen(Boolean(tagPopoverAnchor || anchorPosition));
  }, [tagPopoverAnchor, anchorPosition]);

  const displayTags = trackersTagsMap[tracker.id] || [];

  const handleTagClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setSelectedTagNames(tags);
    const target = event.currentTarget;
    const rect = target.getBoundingClientRect();
    setTagPopoverAnchor(target);
    tagPopoverAnchorRef.current = target;
    // Sauvegarder aussi la position pour fallback
    setAnchorPosition({ top: rect.bottom, left: rect.left });
    setIsOpen(true);
  };

  const handleTagPopoverClose = () => {
    setTagPopoverAnchor(null);
    tagPopoverAnchorRef.current = null;
    setAnchorPosition(undefined);
    setIsOpen(false);
  };

  const handleTagsChange = async (
    newValue: { label: string; value: string }[] | null
  ) => {
    if (!newValue) {
      setSelectedTagNames([]);
      await updateTags([]);
      return;
    }

    // Extraire les noms de tags
    const newTagNames: string[] = newValue.map((item) => item.label);

    // Mettre à jour tous les tags en une seule requête
    setSelectedTagNames(newTagNames);
    
    // Sauvegarder la position de l'ancre avant la mise à jour
    const currentAnchor = tagPopoverAnchorRef.current;
    let savedPosition: { top: number; left: number } | undefined = undefined;
    if (currentAnchor) {
      const rect = currentAnchor.getBoundingClientRect();
      savedPosition = { top: rect.bottom, left: rect.left };
    }
    
    await updateTags(newTagNames);
    
    // Restaurer l'ancre après la mise à jour pour éviter le déplacement
    if (currentAnchor && isOpen && savedPosition) {
      // Utiliser requestAnimationFrame pour s'assurer que le DOM est mis à jour
      requestAnimationFrame(() => {
        // Essayer de retrouver l'élément d'ancrage
        let newAnchor: HTMLElement | null = null;
        
        // D'abord, essayer de réutiliser l'élément s'il existe toujours
        if (currentAnchor && document.body.contains(currentAnchor)) {
          newAnchor = currentAnchor;
        } else {
          // Sinon, chercher l'élément par son attribut data
          // Chercher le bouton d'ajout de tag dans le même conteneur
          const container = currentAnchor.closest('[data-tag-manager]');
          if (container) {
            const tagButton = container.querySelector('[data-tag-button]') as HTMLElement;
            if (tagButton) {
              newAnchor = tagButton;
              const newRect = tagButton.getBoundingClientRect();
              savedPosition = { top: newRect.bottom, left: newRect.left };
            }
          }
        }
        
        if (newAnchor) {
          setTagPopoverAnchor(newAnchor);
          tagPopoverAnchorRef.current = newAnchor;
          setAnchorPosition(undefined); // Utiliser anchorEl si on a trouvé l'élément
          setIsOpen(true);
        } else if (savedPosition) {
          // Si on ne peut pas retrouver l'élément, utiliser la position sauvegardée
          setTagPopoverAnchor(null);
          setAnchorPosition(savedPosition);
          setIsOpen(true);
        }
      });
    }
  };

  // Convert tags to autocomplete options
  const tagOptions = (allTags || []).map((tag) => ({
    label: tag.name,
    value: tag.name,
    color: tag.color,
  }));

  const selectedOptions = selectedTagNames.map((name) => ({
    label: name,
    value: name,
    color: getTagColor(name),
  }));

  return (
    <>
      <div className="flex flex-wrap gap-1 items-center" data-tag-manager>
        {displayTags.length > 0 ? (
          <>
            {displayTags.map((tagName: string) => (
              <Chip
                key={tagName}
                label={tagName}
                size={size === "small" ? "sm" : "md"}
                style={{
                  backgroundColor: getTagColor(tagName),
                  color: "white",
                }}
                onDelete={() => {
                  toggleTag(tagName);
                }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              />
            ))}
            <Tooltip content="Gérer les tags">
              <IconButton
                size="sm"
                variant="ghost"
                onClick={handleTagClick}
                data-tag-button
                className="w-5 h-5 min-w-0"
              >
                <FontAwesomeIcon icon={faPlus} className="text-xs" />
              </IconButton>
            </Tooltip>
          </>
        ) : (
          <Tooltip content="Ajouter un tag">
            <IconButton
              size="sm"
              variant="ghost"
              onClick={handleTagClick}
              data-tag-button
              className="w-5 h-5 min-w-0"
            >
              <FontAwesomeIcon icon={faPlus} className="text-xs" />
            </IconButton>
          </Tooltip>
        )}
      </div>
      <Popover
        open={isOpen}
        anchorEl={tagPopoverAnchor || tagPopoverAnchorRef.current}
        anchorPosition={anchorPosition}
        onClose={handleTagPopoverClose}
        placement="bottom-start"
        offset={[0, 8]}
      >
        <div className="p-4 min-w-[300px] max-w-[400px] bg-surface-elevated rounded-arc-md shadow-arc-lg border border-arc-border">
          <Typography variant="h6" className="mb-3 text-sm font-semibold">
            Gérer les tags
          </Typography>
          <Autocomplete
            multiple
            freeSolo
            options={tagOptions}
            value={selectedOptions}
            onChange={(newValue) => handleTagsChange(newValue as any)}
            placeholder="Sélectionner ou créer des tags..."
            renderTags={(selected, getTagProps) =>
              selected.map((option, index) => (
                <Chip
                  key={option.value}
                  label={option.label}
                  size="sm"
                  style={{
                    backgroundColor: getTagColor(option.label),
                    color: "white",
                  }}
                  onDelete={getTagProps(index).onDelete}
                />
              ))
            }
            renderOption={(option: any, state: any) => (
              <div className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: getTagColor(option.label) }}
                />
                <span>{option.label}</span>
              </div>
            )}
          />
        </div>
      </Popover>
    </>
  );
};

export default TrackerTagManagerInline;
